import { isLoggedIn, getToken } from './authservice';
import { getDashboardHeaderForAPI } from './globalDashboardUtils';

const DEFAULT_RETRIES = 2;
const RETRY_STATUS = [503];
const BASE_DELAY_MS = 400;

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const getHeaders = (headers) => {
  const finalHeaders = headers ? { ...headers } : getDashboardHeaderForAPI();
  if (isLoggedIn()) {
    finalHeaders.Authorization = `Bearer ${getToken()}`;
  }
  if (!finalHeaders['Content-Type']) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  return finalHeaders;
};

const rawFetch = async (url, options) => {
  return fetch(url, options);
};

const fetchWithRetry = async (url, options, retries = DEFAULT_RETRIES, retryOn = RETRY_STATUS) => {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let response;
    try {
      response = await rawFetch(url, options);
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      if (attempt >= retries) throw err;
    }
    if (response) {
      if (!retryOn.includes(response.status) || attempt >= retries) {
        return response;
      }
    }
    attempt++;
    await sleep(BASE_DELAY_MS * attempt);
  }
};

const parseJson = async (response) => {
  let json;
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  if (response.ok) return json;
  const error = json || { status: response.status };
  return Promise.reject(error);
};

const requestJson = async ({
  url,
  method = 'GET',
  payload,
  headers,
  signal,
  retries = DEFAULT_RETRIES,
  retryOn = RETRY_STATUS,
  allowWhenLoggedOut = false
}) => {
  if (!isLoggedIn() && method !== 'GET' && !allowWhenLoggedOut) {
    return Promise.reject({ message: 'Not authenticated' });
  }
  const options = {
    method,
    headers: getHeaders(headers),
    signal
  };
  if (payload !== undefined) {
    options.body = JSON.stringify(payload);
  }
  const response = await fetchWithRetry(url, options, retries, retryOn);
  return parseJson(response);
};

// GET
const GetRequest = (url, signal) =>
  requestJson({ url, method: 'GET', signal })
    .catch((err) => {
      console.log(err);
      throw err;
    });

export default GetRequest;

// POST with callbacks
export const PostRequest = (url, signal, payload, successCallback, errorCallback, forceAllowPost = false) => {
  return requestJson({
    url,
    method: 'POST',
    payload,
    signal,
    allowWhenLoggedOut: forceAllowPost
  }).then(data => {
    successCallback && successCallback(data);
  }).catch(err => {
    if (err?.name === 'AbortError') return;
    console.log(err);
    errorCallback && errorCallback(err);
  });
};

// POST returning a promise result
export const PostRequestResolve = (url, signal, payload, headers) =>
  requestJson({ url, method: 'POST', payload, headers, signal })
    .catch((err) => {
      console.log(err);
      throw err;
    });